import { useState, useEffect, useCallback, useRef } from "react";
import { Form } from "react-admin";
import Button from "@mui/material/Button";
import useDrivePicker from "react-google-drive-picker";
import CADToolSheetLoader from "./CADToolSheetLoader";
import LoadingOverlay from "../../core/LoadingOverlay";
import ConfirmDialog from "../../core/ConfirmDialog";
import ErrorDialog from "../../core/ErrorDialog";
import { config } from "../../../config";
import { makeStyles } from "@mui/styles";
import { calibrationService } from "../../../services/CalibrationService";
import { listInitializer, DropdownWithAutocomplete } from "./DropdownV2";

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    inputRow: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        gap: theme.spacing(2),
        margin: "10px 0",
        width: "100%",
        maxWidth: "700px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },
    buttonRow: {
        display: "flex",
        justifyContent: "center",
        margin: "20px 0",
    },
    googleSheetRow: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        margin: "20px 0",
    },
    textInput: {
        margin: "0 30px",
    },
    error: {
        color: theme.palette.error.main,
    },
    status: {
        marginTop: theme.spacing(1),
        textAlign: "center",
        fontWeight: "bold",
        fontSize: "20px",
        color: theme.palette.success.main,
    },
}));

export default function CADUploadTool() {
    const classes = useStyles();
    const [openPicker, data, _] = useDrivePicker();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorDetail, setErrorDetail] = useState(null);
    const [sheetName, setSheetName] = useState(null);
    const [formattedSheetData, setFormattedSheetData] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [sheetDataLoading, setSheetDataLoading] = useState(false);
    const [clientAssetDataIngressLoading, setClientAssetDataIngressLoading] = useState(false);
    const [token, setToken] = useState(null);
    const [sheetId, setSheetId] = useState(null);
    const [sheetEmbedUrl, setSheetEmbedUrl] = useState(null);
    const [clientName, setClientName] = useState(null);
    const [projectName, setProjectName] = useState(null);
    const [clientOptions, setClientOptions] = useState([]);
    const [projectOptions, setProjectOptions] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [projectsLoading, setProjectsLoading] = useState(false);

    const errorTimeoutRef = useRef(null);
    const statusTimeoutRef = useRef(null);

    // For setting error messages to show for only 10s
    const setErrorWithTimeout = useCallback((error, timeoutInMs) => {
        setErrorMessage(error);

        // Clear any existing timeout
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
        }

        // Clear error state after timeout
        errorTimeoutRef.current = setTimeout(() => setErrorMessage(null), timeoutInMs);
    }, []);

    const formatOptions = useCallback(
        (options) => options.data.map((option) => ({ label: option, value: option })),
        []
    );

    const initializeClients = listInitializer(
        calibrationService.getClients.bind(calibrationService),
        formatOptions,
        setClientOptions,
        () => {},
        "client list",
        setClientsLoading
    );

    const initializeProjects = listInitializer(
        calibrationService.getProjectNames.bind(calibrationService),
        formatOptions,
        setProjectOptions,
        () => {},
        "project list",
        setProjectsLoading
    );

    // For setting the status message of uploading/teardown for only 10s
    const setStatusWithTimeout = useCallback((status, timeoutInMs) => {
        setStatusMessage(status);

        // Clear any existing timeout
        if (statusTimeoutRef.current) {
            clearTimeout(statusTimeoutRef.current);
        }

        // Clear status message state after timeout
        statusTimeoutRef.current = setTimeout(() => setStatusMessage(null), timeoutInMs);
    }, []);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
            if (statusTimeoutRef.current) {
                clearTimeout(statusTimeoutRef.current);
            }
        };
    }, []);

    const handlePayload = (payload) => {
        setFormattedSheetData(payload);
    };

    const handleOpenModal = () => {
        setConfirmModalOpen(true);
    };

    const handleCloseModal = () => {
        setConfirmModalOpen(false);
    };

    const handleConfirm = () => {
        handleSubmitForm();
        setConfirmModalOpen(false);
    };

    const handleSubmitForm = async () => {
        try {
            setClientAssetDataIngressLoading(true);
            await calibrationService.clientAssetDataIngress(clientName, projectName, formattedSheetData);
            setStatusWithTimeout("Client Asset Data uploaded successfully!", 10000);
        } catch (error) {
            console.error(`Error while uploading Client Asset Data: ${error}`);
            let parsedError;
            try {
                parsedError = JSON.parse(error);
            } catch (e) {
                parsedError = error;
            }
            setErrorDetail(parsedError);
            setErrorModalOpen(true);

            // Disables the "Submit Form" button, requiring a sheet-refetch before submitting again
            setFormattedSheetData(null);

            // Unloads the Google Sheet iframe
            setSheetId(null);
        } finally {
            setClientAssetDataIngressLoading(false);
        }
    };

    const handleOpenPicker = () => {
        openPicker({
            clientId: config.googleClientId,
            developerKey: config.googleApiDeveloperKey,
            token: data?.access_token,
            viewId: "SPREADSHEETS",
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: true,
            customScopes: [
                "https://www.googleapis.com/auth/drive.readonly",
                "https://www.googleapis.com/auth/spreadsheets.readonly",
            ],
            callbackFunction: (pickerAction) => {
                if (pickerAction.action === "picked") {
                    setSheetName(pickerAction.docs[0].name);
                    setSheetId(pickerAction.docs[0].id);
                    setSheetEmbedUrl(pickerAction.docs[0].embedUrl);
                }
            },
        });
    };

    useEffect(() => {
        initializeClients();
    }, []);

    useEffect(() => {
        if (clientName) {
            initializeProjects(clientName);
        }
    }, [clientName]);

    useEffect(() => {
        // TODO: We are grabbing the google OAuth token from the picker.
        // We should change the LV Admin User API to return this instead and place that in userService
        if (data) {
            setToken(data.access_token);
        }
    }, [data]);

    return (
        <div style={{ paddingBottom: "750px" }}>
            <h1 style={{ textAlign: "center" }}>Client Asset Data Upload</h1>
            <ConfirmDialog
                open={confirmModalOpen}
                title="Confirm Upload to Calibration Platform"
                handleClose={handleCloseModal}
                handleConfirm={handleConfirm}
                message={`Are you sure you want to upload for client ${clientName} and project ${projectName}? \n This will upload for the following sheet:`}
                newLineMessage={`${sheetName}`}
            />
            <ErrorDialog
                errorModalOpen={errorModalOpen}
                setErrorModalOpen={setErrorModalOpen}
                title={"Error while uploading to Client Asset Data."}
                handleClose={() => setErrorModalOpen(false)}
                messagePrefix={`For client ${clientName} and project ${projectName}:`}
                message={`${JSON.stringify(errorDetail, null, 2)}`}
            />
            <Form className={classes.formContainer}>
                {errorMessage && (
                    <p className={classes.error}>
                        {typeof errorMessage === "object" ? JSON.stringify(errorMessage) : errorMessage}
                    </p>
                )}
                {statusMessage && <p className={classes.status}>{statusMessage}</p>}
                <LoadingOverlay isLoading={sheetDataLoading} message="Loading Sheet Data..." />
                <LoadingOverlay isLoading={clientAssetDataIngressLoading} message="Uploading Client Asset Data..." />
                <div className={classes.inputRow}>
                    <DropdownWithAutocomplete
                        label="Client"
                        value={clientName}
                        setValue={setClientName}
                        refreshOptions={initializeClients}
                        refreshDisabled={false}
                        options={clientOptions}
                        freeSolo={true}
                        isLoading={clientsLoading}
                        styleClass={{
                            flex: 1,
                            minWidth: "150px",
                            maxWidth: "300px",
                        }}
                    />
                    <DropdownWithAutocomplete
                        label="Project"
                        value={projectName}
                        setValue={setProjectName}
                        refreshOptions={() => initializeProjects(clientName)}
                        refreshDisabled={false}
                        options={projectOptions}
                        freeSolo={true}
                        isLoading={projectsLoading}
                        styleClass={{
                            flex: 1,
                            minWidth: "150px",
                            maxWidth: "300px",
                        }}
                    />
                </div>
                <div className={classes.buttonRow}>
                    <Button variant="contained" onClick={() => handleOpenPicker()}>
                        Get Sheet From Google Drive
                    </Button>
                </div>
                <div className={classes.googleSheetRow}>
                    {sheetId && sheetEmbedUrl && (
                        <CADToolSheetLoader
                            sheetId={sheetId}
                            sheetEmbedUrl={sheetEmbedUrl}
                            setSheetEmbedUrl={setSheetEmbedUrl}
                            token={token}
                            onPayloadReceived={handlePayload}
                            setErrorWithTimeout={setErrorWithTimeout}
                            setSheetDataLoading={setSheetDataLoading}
                        />
                    )}
                </div>
                <div className={classes.buttonRow}>
                    <Button
                        variant="contained"
                        disabled={!formattedSheetData || !clientName || !projectName}
                        onClick={handleOpenModal}
                    >
                        Submit Form
                    </Button>
                </div>
            </Form>
        </div>
    );
}
